/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { html, unsafeCSS, nothing, PropertyValues } from 'lit';
import { property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { ContextConsumer } from '@lit/context';
import { ScopedRegistryHost } from '@lit-labs/scoped-registry-mixin';
import { LeafElement } from '@cigna/dcws_leaf-web-components/components/leaf-element.js';
import { LeafButton } from '@cigna/dcws_leaf-web-components/components/button/button.component.js';
import { CHCArticle } from '@cigna/dcws_leaf-web-components/chc-components/article/article.component.js';
import { emit } from '@cigna/dcws_leaf-web-components/utilities/events.js';
import { I18nMixin } from '../../utils/i18n-mixin.js';
import { stripHtmlFromLink } from '../../utils/fix-links.js';
import { endpointsContext } from '../contexts.js';
import { RelatedLinkService } from './service.js';
import styles from './related-links.scss?inline';

type RelatedLinksData = {
  show_all_articles: string;
  results_count: string;
};

export class CHCRelatedLinks extends ScopedRegistryHost(I18nMixin(LeafElement)) {
  static override styles = unsafeCSS(styles);
  static elementDefinitions = {
    'leaf-button': LeafButton,
    'chc-article': CHCArticle,
  };
  protected override ns = 'related-links';
  private data?: RelatedLinksData;
  language = location.pathname.includes('es-us') ? 'es-us' : 'en-us';
  loading = true;
  error = false;

  @property({ reflect: true })
  relateby!: string;

  @property({ reflect: true })
  domain = 'www-cigna-com';

  @property({ reflect: true, type: Number })
  limit = 5;

  @property({ type: Boolean })
  private showAll = false;

  @state()
  relatedLinkService!: RelatedLinkService;

  @state()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  relatedLinks: Array<any> = [];

  @state()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  moreRelatedLinks: Array<any> = [];

  @state()
  totalArticles = 0;

  constructor() {
    super();
    new ContextConsumer(this, {
      context: endpointsContext,
      callback: (value) => {
        this.relatedLinkService = new RelatedLinkService(value.relatedLinks!);
      },
    });
  }

  override onNamespacesLoaded = () => {
    this.data = this.i18n?.getLanguageData(this.ns) as RelatedLinksData;
  };

  protected override async firstUpdated(_changedProperties: PropertyValues): Promise<void> {
    try {
      const relatedLinks = await this.relatedLinkService.getRelatedLinks(
        location.pathname,
        this.relateby,
        0,
        this.domain,
        this.language,
      );
      this.totalArticles = relatedLinks.length;
      this.relatedLinks = relatedLinks.slice(0, this.limit);
      this.moreRelatedLinks = relatedLinks.slice(this.limit, relatedLinks.length);
      this.loading = false;

      emit('chcapp-related-links:service', { detail: { relatedLinks } });
    } catch (error) {
      emit('chcapp-related-links:service', { detail: { error: true } });
      this.error = true;
      this.loading = false;
    }
  }

  async showMore() {
    this.relatedLinks = [...this.relatedLinks, ...this.moreRelatedLinks];
    this.showAll = true;
  }

  override render() {
    if (!this.i18n || this.i18n.loading || this.loading) {
      return html`<slot name="loading"></slot>`;
    }

    if (this.i18n?.error || this.error) {
      return html`<slot name="error"></slot>`;
    }

    return html`
      <div class="chcapp-related-links">
        <slot name="heading"></slot>
        ${this.relatedLinks.map(
          ({ content_type, authored_date, summaryheading, summary, path }) =>
            html` <chc-article>
              <span slot="content-type">${content_type}</span>
              <span slot="authored-date"
                >${authored_date
                  ? new Date(authored_date.replace(/-/g, '/')).toLocaleString('default', {
                      month: 'long',
                      year: 'numeric',
                    })
                  : nothing}</span
              >
              <h3 slot="heading">
                ${path
                  ? html`<a href="${stripHtmlFromLink(path)}">${unsafeHTML(summaryheading)}</a>`
                  : unsafeHTML(summaryheading)}
              </h3>
              <p slot="content">${summary}</p>
            </chc-article>`,
        )}
      </div>
      ${!this.showAll && this.totalArticles > this.limit
        ? html`<leaf-button variant="secondary" @click="${this.showMore}">${this.data?.show_all_articles}</leaf-button>`
        : nothing}
    `;
  }
}
